import { Flex, Progress, Typography } from 'antd';

import Show from 'components/Show';

import {
  calculateDatePercentage,
  getTimeRemainingMessage,
} from 'screens/task-management/util';
import { renderDate } from 'utils/helpers';

import type { TaskStatus } from 'types/common';
import DateTime from 'utils/datetime';
import { Task } from '../types';

interface TaskPlannedDateProps {
  status: TaskStatus;
  style?: React.CSSProperties;
  date: string;
}

export const TaskPlannedDate = ({ date, status }: TaskPlannedDateProps) => {
  const shouldShowTimeRemainingStatus =
    status !== 'UPCOMING' && status !== 'COMPLETED';

  return (
    <Flex vertical>
      <Typography.Text>{renderDate(date)}</Typography.Text>
      <Show when={shouldShowTimeRemainingStatus}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {getTimeRemainingMessage(date)}
        </Typography.Text>
      </Show>
    </Flex>
  );
};

interface TaskTimeWindowProgressProps {
  record: Task;
}

export const TaskTimeWindowProgress = ({
  record,
}: TaskTimeWindowProgressProps) => {
  const { plannedDate, startDate, endDate } = record;

  if (!startDate || !endDate || !plannedDate)
    return 'Not applicable for this task';

  const progress = calculateDatePercentage(startDate, endDate);

  const daysRemainingToEndDate = Math.max(
    DateTime.diff(endDate, DateTime.now(), 'days'),
    0,
  );

  const calculateProgressPercentage = () => {
    const status = record?.status;
    if (status === 'UPCOMING') {
      return 0;
    }
    if (daysRemainingToEndDate <= 0) {
      return 100;
    }
    return progress;
  };

  return (
    <Flex vertical>
      <Typography.Text
        title={`${renderDate(startDate)} - ${renderDate(endDate)}`}
      >
        {record?.status === 'UPCOMING'
          ? 'Not started yet'
          : `${daysRemainingToEndDate} days remaining`}
      </Typography.Text>
      <Progress
        showInfo={false}
        percent={calculateProgressPercentage()}
        size="default"
        strokeColor={daysRemainingToEndDate > 0 ? '' : 'red'}
      />
    </Flex>
  );
};
